/* eslint-disable react/prop-types */
/**
 * Admin → Operators — maintain the shared operator master (BD.Admin).
 *
 * Operators are the vocabulary behind the Well's `operator_id` field (see the
 * deal-detail Wells tab, whose Operator picker reads the active-only
 * `listOperators` list). This admin screen is the single place to curate that
 * master list: add a new operator, rename an existing one, and activate /
 * deactivate (soft delete) — there is NO hard delete, because wells reference
 * operators by id and history must stay intact.
 *
 * Deactivating flips `active=false`: the row stays listed here (greyed + tagged
 * INACTIVE) so an admin can reactivate it, but it drops out of the active-only
 * Well Operator picker. A Popconfirm guards deactivation; reactivation is a
 * single click.
 *
 * OPERATOR CODE + DUPLICATE NAMES
 * Each operator may carry an `operator_code` — the code it came over with from
 * the upstream internal system of record (de-hub migration 065; the 635-row
 * bulk load is migration 066). The code, not the name, is the unique key:
 * migration 065 dropped the unique index on `operators.name` because the
 * upstream master holds eight names under two codes each (WARWICK JUPITER LLC
 * is both `102` and `WAR200`, and so on). Consequences visible here:
 *   * The Code column is the disambiguator when two rows share a name.
 *   * The server no longer rejects a duplicate NAME, so the Add/Rename modal
 *     shows a non-blocking warning listing the operators that already use the
 *     name typed — a hint, not a gate, since duplicates are sometimes correct.
 *   * A 409 from the API now means the CODE is taken, and is surfaced inline on
 *     the code field.
 *
 * Gating is UI/UX only — the de-hub `/operators` endpoints are the real boundary
 * (`PUT /operators/{id}` is admin-only). We redirect non-admins to `/` as
 * defense-in-depth for the route, mirroring the Entities page.
 */
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from "@azure/msal-react";
import {
  Alert,
  Button,
  Checkbox,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Space,
  Tag,
  Typography,
  message,
  notification,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  StopOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router";
import useMeasure from "react-use-measure";

import {
  WigAntLayout,
  WigAntTable,
} from "@warwick-investment-group/wig-react-library";

import SignIn from "../../signIn";
import { AppContext } from "../../../AppContextProvider";
import usePermissions from "../../../hooks/usePermissions";
import dealsService from "../../../services/dealsService";
import { apiErrorText } from "../../../utils/apiError";
import { findNameMatches } from "../../../utils/normalizeLookupName";

const { Title } = Typography;

// Empty form template — `name` and `operator_code` are the editable fields.
const EMPTY_OPERATOR = { name: "", operator_code: "" };

/**
 * OperatorFormModal — single Add/Edit modal. The parent owns visibility and
 * the row under edit; this component is layout + validation + the duplicate
 * name warning only.
 *
 * `existingRows` is the full operator list from the parent, used solely to warn
 * (never to block) when the typed name already belongs to another operator.
 */
const OperatorFormModal = ({
  open,
  mode, // 'add' | 'edit'
  initialValues,
  saving,
  existingRows,
  editingId,
  onCancel,
  onSubmit,
}) => {
  const [form] = Form.useForm();
  // Mirror the two fields so the duplicate warning re-renders as the admin
  // types. Form.useWatch keeps these in sync without a controlled-input dance.
  // The code is watched too because it changes what saving actually DOES (see
  // `outcome` below), not just what gets stored.
  const typedName = Form.useWatch("name", form);
  const typedCode = Form.useWatch("operator_code", form);

  // Re-seed the form whenever the modal opens or the row under edit changes.
  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue(initialValues || EMPTY_OPERATOR);
    }
  }, [open, initialValues, form]);

  // Operators already using the typed name (server-normalized comparison),
  // excluding the row being renamed. Duplicate names are LEGAL server-side
  // since de-hub migration 065, so this is advisory: it tells the admin what
  // they're about to sit next to, and shows each match's code because that is
  // the only thing distinguishing two same-named operators.
  const duplicateMatches = useMemo(
    () => findNameMatches(typedName, existingRows, editingId),
    [typedName, existingRows, editingId]
  );

  // What will ACTUALLY happen on save. The three cases differ because
  // POST /operators picks its idempotency key from the body:
  //   * Add with NO code   — dedupes on the normalized name among ACTIVE rows,
  //       so a live match is RETURNED and no new row appears. If every match is
  //       inactive the insert goes ahead and a duplicate name results.
  //   * Add WITH a code    — the code is the key, so the name is not consulted
  //       and a second row sharing the name is created.
  //   * Edit (rename)      — PUT never dedupes on name (migration 065), so the
  //       rename lands and two operators end up sharing a name.
  // Saying "this will create a duplicate" in all three would be wrong for the
  // first, which is the most common way an admin reaches this warning.
  const outcome = useMemo(() => {
    const codeEntered = Boolean(String(typedCode ?? "").trim());
    if (mode === "edit") return "rename";
    if (codeEntered) return "duplicate";
    return duplicateMatches.some((row) => row.active)
      ? "returns-existing"
      : "duplicate";
  }, [mode, typedCode, duplicateMatches]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      await onSubmit(values, { setFieldError: form.setFields });
    } catch {
      // validation / onSubmit errors surfaced inline by AntD or by the parent.
    }
  };

  return (
    <Modal
      open={open}
      title={mode === "add" ? "Add operator" : "Edit operator"}
      onCancel={onCancel}
      onOk={handleOk}
      confirmLoading={saving}
      okText={mode === "add" ? "Create" : "Save"}
      destroyOnHidden
      width={520}
    >
      <Form form={form} layout="vertical" initialValues={EMPTY_OPERATOR}>
        <Form.Item
          label="Name"
          name="name"
          rules={[{ required: true, message: "Name is required" }]}
          tooltip="Display name. Names are NOT required to be unique — two operators may share a name when they are distinct records upstream, told apart by their code."
        >
          <Input autoFocus placeholder="e.g. Devon Energy" />
        </Form.Item>

        {/* Non-blocking duplicate-name notice. Deliberately a warning and not
            a validation rule: the API accepts duplicate names by design, and
            the admin is the one who knows whether this one is intentional. */}
        {duplicateMatches.length > 0 && (
          <Alert
            type={outcome === "returns-existing" ? "info" : "warning"}
            showIcon
            style={{ marginBottom: 16 }}
            message={
              duplicateMatches.length === 1
                ? "An operator already uses this name"
                : `${duplicateMatches.length} operators already use this name`
            }
            description={
              <React.Fragment>
                <div style={{ marginBottom: 4 }}>
                  {outcome === "returns-existing" &&
                    "With no code entered, saving will just return the existing operator below — no new record is created. Enter a code if this really is a different operator."}
                  {outcome === "duplicate" &&
                    "Saving will create a second operator with the same name. That is allowed — some operators are distinct records upstream that happen to share a name — but the Well Operator picker will show both, told apart only by their code."}
                  {outcome === "rename" &&
                    "Saving will leave two operators sharing this name. That is allowed — the Well Operator picker tells them apart by their code — but check the code below is right first."}
                </div>
                <ul style={{ margin: "4px 0 0 0", paddingLeft: 18 }}>
                  {duplicateMatches.map((row) => (
                    <li key={row.id}>
                      {row.name}
                      {row.operator_code ? (
                        <Typography.Text type="secondary">
                          {" "}
                          — code {row.operator_code}
                        </Typography.Text>
                      ) : (
                        <Typography.Text type="secondary"> — no code</Typography.Text>
                      )}
                      {!row.active && (
                        <Tag color="default" style={{ marginLeft: 6 }}>
                          INACTIVE
                        </Tag>
                      )}
                    </li>
                  ))}
                </ul>
              </React.Fragment>
            }
          />
        )}

        <Form.Item
          label="Operator code"
          name="operator_code"
          tooltip="The operator's code in the upstream internal system of record (e.g. WAR200, 103882). Optional — operators added here by hand may not have one. Must be unique: reusing another operator's code is rejected."
        >
          <Input placeholder="e.g. WAR200 (optional)" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const Operators = () => {
  const navigate = useNavigate();
  const [ref, bounds] = useMeasure();
  const { token, getToken, isTokenExpired, user, theme } =
    useContext(AppContext);
  const { can } = usePermissions();
  const allowed = can("admin.operators");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(true);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add"); // 'add' | 'edit'
  const [editingRow, setEditingRow] = useState(null);
  const [saving, setSaving] = useState(false);

  // Redirect users who lack the permission (defense-in-depth for the route;
  // the API is the real boundary).
  useEffect(() => {
    if (!allowed) navigate("/", { replace: true });
  }, [allowed, navigate]);

  const getAuth = useCallback(async () => {
    const userOid = user?.idTokenClaims?.oid || user?.localAccountId;
    if (!userOid) throw new Error("OID not found in user claims.");
    let tk = token;
    if (!tk || isTokenExpired(tk)) tk = await getToken();
    return { token: tk, userOid };
  }, [token, getToken, isTokenExpired, user]);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMsg(null);
      const { token: tk, userOid } = await getAuth();
      const resp = await dealsService.listAllOperators({
        token: tk,
        userOid,
        includeInactive,
      });
      const data = resp?.data?.data;
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error loading operators:", err);
      setErrorMsg(err?.message || String(err));
    } finally {
      setLoading(false);
    }
  }, [getAuth, includeInactive]);

  useEffect(() => {
    if (allowed) refresh();
  }, [allowed, refresh]);

  // ---------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------

  const handleCreate = async (values, helpers) => {
    setSaving(true);
    try {
      const { token: tk, userOid } = await getAuth();
      await dealsService.createOperator({
        token: tk,
        userOid,
        name: values.name?.trim(),
        // Blank is dropped from the body by the service — an operator added
        // here need not have an upstream code.
        operatorCode: values.operator_code,
      });
      message.success("Operator created");
      setModalOpen(false);
      await refresh();
    } catch (err) {
      handleSaveError(err, helpers);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (values, helpers) => {
    setSaving(true);
    try {
      const { token: tk, userOid } = await getAuth();
      // `operator_code` is always sent so the field can CLEAR a code: an
      // explicit null is distinguishable server-side from an omitted key, while
      // a blank string normalizes to null there too.
      const code = values.operator_code?.trim();
      await dealsService.updateOperator({
        token: tk,
        userOid,
        id: editingRow.id,
        body: { name: values.name?.trim(), operator_code: code || null },
      });
      message.success("Operator saved");
      setModalOpen(false);
      await refresh();
    } catch (err) {
      handleSaveError(err, helpers);
    } finally {
      setSaving(false);
    }
  };

  // Surface a save error either inline on the offending field or as a
  // top-level notification.
  //
  // A 409 means the OPERATOR CODE is already assigned to another operator —
  // `operator_code` is the only unique key left (de-hub migration 065 dropped
  // the unique index on `name`), so the error belongs on that field. Duplicate
  // names never reach here at all; they are handled by the modal's advisory
  // warning instead.
  const handleSaveError = (err, helpers) => {
    const status = err?.response?.status;
    const detail = apiErrorText(err, { fallback: "Save failed" });

    if (status === 409) {
      helpers?.setFieldError?.([
        { name: "operator_code", errors: [String(detail)] },
      ]);
      return;
    }
    notification.error({
      message: "Operator save failed",
      description: String(detail),
    });
  };

  // Activate / deactivate — a soft toggle on `active`. No hard delete.
  const handleToggleActive = async (row) => {
    try {
      const { token: tk, userOid } = await getAuth();
      await dealsService.updateOperator({
        token: tk,
        userOid,
        id: row.id,
        body: { active: !row.active },
      });
      message.success(
        row.active ? "Operator deactivated" : "Operator activated"
      );
      await refresh();
    } catch (err) {
      const detail = apiErrorText(err);
      notification.error({
        message: row.active
          ? "Failed to deactivate operator"
          : "Failed to activate operator",
        description: detail,
      });
    }
  };

  // ---------------------------------------------------------------------
  // Grid wiring
  // ---------------------------------------------------------------------

  const openAdd = () => {
    setModalMode("add");
    setEditingRow(null);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setModalMode("edit");
    setEditingRow(row);
    setModalOpen(true);
  };

  const fmtDate = (v) => {
    if (!v) return "";
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
  };

  const columns = useMemo(
    () => [
      {
        dataIndex: "name",
        title: "Name",
        key: "name",
        sorter: (a, b) =>
          String(a?.name ?? "").localeCompare(String(b?.name ?? "")),
        render: (value, row) =>
          row?.active ? (
            value
          ) : (
            <span style={{ opacity: 0.55 }}>{value}</span>
          ),
      },
      {
        // The upstream operator code. Not just informational — when two rows
        // share a name (legal since de-hub migration 065) this column is the
        // only thing that tells them apart.
        dataIndex: "operator_code",
        title: "Code",
        key: "operator_code",
        width: 130,
        sorter: (a, b) =>
          String(a?.operator_code ?? "").localeCompare(
            String(b?.operator_code ?? "")
          ),
        render: (value, row) => {
          if (!value) {
            return (
              <Typography.Text type="secondary" italic>
                —
              </Typography.Text>
            );
          }
          return row?.active ? (
            value
          ) : (
            <span style={{ opacity: 0.55 }}>{value}</span>
          );
        },
      },
      {
        dataIndex: "active",
        title: "Active",
        key: "active",
        width: 110,
        sorter: (a, b) => Number(a?.active) - Number(b?.active),
        render: (value) => (
          <Tag color={value ? "green" : "default"}>
            {value ? "ACTIVE" : "INACTIVE"}
          </Tag>
        ),
      },
      {
        dataIndex: "created_by",
        title: "Created by",
        key: "created_by",
        width: 160,
      },
      {
        dataIndex: "created_at",
        title: "Created",
        key: "created_at",
        width: 190,
        sorter: (a, b) =>
          String(a?.created_at ?? "").localeCompare(
            String(b?.created_at ?? "")
          ),
        render: (value) => fmtDate(value),
      },
      {
        dataIndex: "updated_by",
        title: "Updated by",
        key: "updated_by",
        width: 160,
      },
      {
        dataIndex: "updated_at",
        title: "Updated",
        key: "updated_at",
        width: 190,
        sorter: (a, b) =>
          String(a?.updated_at ?? "").localeCompare(
            String(b?.updated_at ?? "")
          ),
        render: (value) => fmtDate(value),
      },
      {
        title: "Actions",
        key: "actions",
        width: 130,
        fixed: "right",
        align: "center",
        render: (_, row) => {
          if (!row) return null;
          return (
            <Space size="small" onClick={(e) => e.stopPropagation()}>
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => openEdit(row)}
                aria-label={`Edit ${row.name}${
                  row.operator_code ? ` (${row.operator_code})` : ""
                }`}
              />
              {row.active ? (
                <Popconfirm
                  title="Deactivate operator?"
                  // Include the code: with duplicate names legal, "Deactivate
                  // WARWICK JUPITER LLC?" alone doesn't say WHICH one.
                  description={`Deactivate "${row.name}"${
                    row.operator_code ? ` (code ${row.operator_code})` : ""
                  }? It stays on file but won't appear in the Well Operator picker.`}
                  okText="Deactivate"
                  okType="danger"
                  cancelText="Cancel"
                  onConfirm={() => handleToggleActive(row)}
                >
                  <Button
                    size="small"
                    danger
                    icon={<StopOutlined />}
                    aria-label={`Deactivate ${row.name}${
                      row.operator_code ? ` (${row.operator_code})` : ""
                    }`}
                  />
                </Popconfirm>
              ) : (
                <Button
                  size="small"
                  icon={<CheckCircleOutlined />}
                  onClick={() => handleToggleActive(row)}
                  aria-label={`Activate ${row.name}${
                    row.operator_code ? ` (${row.operator_code})` : ""
                  }`}
                />
              )}
            </Space>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Initial values for the modal — for Edit we hydrate name + code from the
  // row. A null code becomes "" so the Input stays controlled and clearing it
  // round-trips as an explicit null on save.
  const modalInitialValues = useMemo(() => {
    if (modalMode === "edit" && editingRow) {
      return {
        name: editingRow.name ?? "",
        operator_code: editingRow.operator_code ?? "",
      };
    }
    return EMPTY_OPERATOR;
  }, [modalMode, editingRow]);

  const gridHeight = Math.max(bounds.height - 220, 400);

  if (!allowed) return null;

  return (
    <React.Fragment>
      <AuthenticatedTemplate>
        <Row ref={ref}>
          <WigAntLayout
            darkMode={theme === "dark"}
            layoutStyle={{
              height: "93vh",
              width: "99vw",
              margin: 0,
              padding: 0,
              overflow: "hidden",
            }}
            contentStyle={{
              minHeight: "70vh",
              overflow: "auto",
              padding: 12,
              margin: 0,
            }}
            showFooter={false}
            showHeader={false}
            siderProps={{ collapsible: true, collapsed: true, width: 0 }}
            showDefaultMenu={false}
            content={
              <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <Row justify="space-between" align="middle">
                  <Title level={3} style={{ margin: 0 }}>
                    Operators
                  </Title>
                  <Space>
                    <Checkbox
                      checked={includeInactive}
                      onChange={(e) => setIncludeInactive(e.target.checked)}
                    >
                      Show inactive
                    </Checkbox>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={openAdd}
                    >
                      Add Operator
                    </Button>
                  </Space>
                </Row>

                {errorMsg && (
                  <Alert
                    type="error"
                    showIcon
                    message="Failed to load operators"
                    description={errorMsg}
                  />
                )}

                {!loading && rows.length === 0 && !errorMsg ? (
                  <Alert
                    type="info"
                    showIcon
                    message="No operators yet"
                    description="There are no operator records. Click Add Operator to create the first one."
                  />
                ) : (
                  <WigAntTable
                    dataSource={rows}
                    columns={columns}
                    rowKey={(record) =>
                      record?.id != null ? String(record.id) : undefined
                    }
                    loading={loading}
                    showSearch
                    searchWidth={300}
                    showActionButton={false}
                    showExport
                    exportOptions={{ filename: "operators" }}
                    showPagination
                    pagination={{
                      defaultPageSize: 100,
                      showSizeChanger: true,
                      pageSizeOptions: ["10", "25", "50", "100"],
                    }}
                    tableWidth="100%"
                    scroll={{ y: gridHeight }}
                  />
                )}

                <OperatorFormModal
                  open={modalOpen}
                  mode={modalMode}
                  initialValues={modalInitialValues}
                  saving={saving}
                  // The loaded list doubles as the duplicate-name lookup. It
                  // reflects the `includeInactive` toggle, so with inactive
                  // rows hidden the warning can only cite active operators —
                  // acceptable for an advisory hint, and the default is on.
                  existingRows={rows}
                  editingId={modalMode === "edit" ? editingRow?.id : undefined}
                  onCancel={() => setModalOpen(false)}
                  onSubmit={modalMode === "add" ? handleCreate : handleEdit}
                />
              </Space>
            }
          />
        </Row>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <SignIn />
      </UnauthenticatedTemplate>
    </React.Fragment>
  );
};

export default Operators;
